import type {
  FetchGroceryByIdArgs,
  UnarchiveGroceryArgs,
  ArchiveGroceryArgs,
  FetchGroceriesArgs,
  CreateGroceryArgs,
  EditGroceryArgs,
} from './types/groceries.types';
import type {
  FetchSignedUploadUrlArgs,
  UiSignedUploadUrl,
} from './types/files.types';
import type { ApiGrocery } from '../schemas/groceries.schema';

import {
  createGroceryApiArgs,
  mapGroceryFromApi,
} from './mappers/groceries.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';

export const fetchGrocerySignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchSignedUploadUrlArgs) => {
  const response = await get<{ data: UiSignedUploadUrl }>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/groceries/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export const createGrocery = async ({
  locale,
  organizationId,
  userUnitSettings,
  ...args
}: CreateGroceryArgs) => {
  const response = await post<ApiGrocery>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/groceries`,
    createGroceryApiArgs(args),
  );
  if (!response?.data) return null;
  return mapGroceryFromApi(response.data, locale, userUnitSettings);
};

export const editGrocery = async ({
  id,
  locale,
  organizationId,
  userUnitSettings,
  ...args
}: EditGroceryArgs) => {
  const response = await patch<ApiGrocery>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/groceries/${id}`,
    createGroceryApiArgs(args),
  );
  if (!response?.data) return null;
  return mapGroceryFromApi(response.data, locale, userUnitSettings);
};

export const fetchGroceries = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
  userUnitSettings,
}: FetchGroceriesArgs) => {
  const { search, ...restTableFilters } = tableFilters;
  const { authorIds, isDeleted, sources, tags } = contentFilters ?? {};

  const response = await postWithPagination<ApiGrocery[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/groceries/list`,
    {
      ...restTableFilters,
      ...(search ? { search } : {}),
      ...(tags ? { tagIds: tags } : {}),
      authorIds,
      isDeleted,
      sources,
    },
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response.data.map((grocery) =>
      mapGroceryFromApi(grocery, locale, userUnitSettings),
    ),
  };
};

export const fetchGroceryById = async ({
  id,
  locale,
  organizationId,
  userUnitSettings,
}: FetchGroceryByIdArgs) => {
  const response = await get<{ data: ApiGrocery }>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/groceries/${id}`,
  );
  if (!response?.data) return null;
  return mapGroceryFromApi(response.data, locale, userUnitSettings);
};

export const archiveGrocery = async ({
  id,
  organizationId,
}: ArchiveGroceryArgs) => {
  await destroy(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/groceries/${id}`,
  );
  return true;
};

export const unarchiveGrocery = async ({
  id,
  organizationId,
}: UnarchiveGroceryArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/groceries/${id}/restore`,
  );
  return true;
};
